import { useState, useRef, useCallback, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export function ImageLightbox({ images, initialIndex = 0, onClose }) {
  const [currentIdx, setCurrentIdx] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = useState(false);

  // Touch tracking refs
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);
  const lastTapRef = useRef(0);
  const pinchStartDistRef = useRef(null);
  const pinchStartScaleRef = useRef(1);
  const panStartRef = useRef(null);
  const isDraggingRef = useRef(false);

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setIsZoomed(false);
  }, []);

  const goTo = useCallback((idx) => {
    resetZoom();
    setCurrentIdx(idx);
  }, [resetZoom]);

  const goPrev = useCallback(() => {
    goTo((currentIdx - 1 + images.length) % images.length);
  }, [currentIdx, images.length, goTo]);

  const goNext = useCallback(() => {
    goTo((currentIdx + 1) % images.length);
  }, [currentIdx, images.length, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  // Distance between two touch points
  const getTouchDist = (t1, t2) => {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch start
      pinchStartDistRef.current = getTouchDist(e.touches[0], e.touches[1]);
      pinchStartScaleRef.current = scale;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: now };

      // Double tap detection
      if (now - lastTapRef.current < 300) {
        e.preventDefault();
        if (isZoomed) {
          resetZoom();
        } else {
          setScale(2.5);
          setIsZoomed(true);
        }
        lastTapRef.current = 0;
        return;
      }
      lastTapRef.current = now;

      // Pan start (when zoomed)
      if (isZoomed) {
        panStartRef.current = { x: translate.x, y: translate.y };
        isDraggingRef.current = true;
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartDistRef.current) {
      // Pinch zoom
      e.preventDefault();
      const dist = getTouchDist(e.touches[0], e.touches[1]);
      const newScale = Math.max(1, Math.min(5, pinchStartScaleRef.current * (dist / pinchStartDistRef.current)));
      setScale(newScale);
      setIsZoomed(newScale > 1.1);
      if (newScale <= 1.05) {
        setTranslate({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && isDraggingRef.current && panStartRef.current) {
      // Pan while zoomed
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      setTranslate({
        x: panStartRef.current.x + dx,
        y: panStartRef.current.y + dy,
      });
    }
  };

  const handleTouchEnd = (e) => {
    // Pinch end
    if (pinchStartDistRef.current && e.touches.length < 2) {
      pinchStartDistRef.current = null;
      if (scale <= 1.05) resetZoom();
    }

    // Swipe detection (only when not zoomed)
    if (!isZoomed && touchStartRef.current && e.changedTouches.length === 1) {
      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartRef.current.x;
      const dy = touch.clientY - touchStartRef.current.y;
      const dt = Date.now() - touchStartRef.current.time;

      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) && dt < 400) {
        if (dx > 0) goPrev();
        else goNext();
      }
    }

    isDraggingRef.current = false;
    panStartRef.current = null;
    touchStartRef.current = null;
  };

  // Mouse double-click to zoom
  const handleDoubleClick = () => {
    if (isZoomed) {
      resetZoom();
    } else {
      setScale(2.5);
      setIsZoomed(true);
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      data-testid="image-lightbox"
      onClick={(e) => {
        if (e.target === containerRef.current) onClose();
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        data-testid="lightbox-close-btn"
      >
        <X className="h-6 w-6 text-white" />
      </button>

      {/* Image counter */}
      {images.length > 1 && (
        <div className="absolute top-4 left-4 z-50 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm font-medium">
          {currentIdx + 1} / {images.length}
        </div>
      )}

      {/* Prev/Next arrows (desktop) */}
      {images.length > 1 && !isZoomed && (
        <>
          <button
            onClick={goPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors hidden sm:flex"
            data-testid="lightbox-prev-btn"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors hidden sm:flex"
            data-testid="lightbox-next-btn"
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="w-full h-full flex items-center justify-center overflow-hidden touch-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
      >
        <img
          src={images[currentIdx]}
          alt={`Image ${currentIdx + 1}`}
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: isDraggingRef.current || pinchStartDistRef.current ? 'none' : 'transform 0.2s ease-out',
          }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Zoom hint (shown briefly) */}
      {!isZoomed && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/60 text-xs pointer-events-none">
          Double-tap to zoom
        </div>
      )}
    </div>
  );
}
